import { useState, useEffect, useCallback } from "react";
import { SupportService, type DiagnosticUploadInfo } from "@/services/SupportService";
import { SupportUploadCard } from "./SupportUploadCard";

interface SupportUploadsSubTabProps {
  userId: number;
  onCountChange?: (count: number) => void;
  onUploadSelect?: (uploadId: string | null) => void;
}

export const SupportUploadsSubTab: React.FC<SupportUploadsSubTabProps> = ({ userId, onCountChange, onUploadSelect }) => {
  const [uploads, setUploads] = useState<DiagnosticUploadInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [platformFilter, setPlatformFilter] = useState<string>("");
  const [analyzedFilter, setAnalyzedFilter] = useState<string>("");
  const [expandedUploadId, setExpandedUploadId] = useState<string | null>(null);

  const loadUploads = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await SupportService.listUserUploads(userId, {
        platform: platformFilter || undefined,
        analyzed: analyzedFilter === "" ? undefined : analyzedFilter === "true",
        sortBy: "created_at",
        sortOrder: "desc",
        limit: 100,
      });
      setUploads(data);
      onCountChange?.(data.length);
    } catch (err) {
      console.error("Error loading support uploads:", err);
      setError("Failed to load support uploads. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [userId, platformFilter, analyzedFilter, onCountChange]);

  useEffect(() => {
    loadUploads();
  }, [loadUploads]);

  // Handle deep link for upload expansion
  useEffect(() => {
    const deeplinkUploadId = sessionStorage.getItem("deeplink-uploadId");
    if (deeplinkUploadId && uploads.length > 0) {
      const exists = uploads.some(u => u.uploadId === deeplinkUploadId);
      if (exists) {
        setExpandedUploadId(deeplinkUploadId);
      }
      sessionStorage.removeItem("deeplink-uploadId");
    }
  }, [uploads]);

  const handleRefresh = () => {
    loadUploads();
  };

  const handleUploadExpand = (uploadId: string, isExpanded: boolean) => {
    const newExpandedId = isExpanded ? uploadId : null;
    setExpandedUploadId(newExpandedId);
    onUploadSelect?.(newExpandedId);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <svg className="w-12 h-12 text-red-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p className="text-sm text-gray-600 mb-4">{error}</p>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 text-sm font-medium text-cyan-600 hover:text-cyan-700"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with filters */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <select
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Platforms</option>
            <option value="wearos">Wear OS</option>
            <option value="ios_watch">Apple Watch</option>
            <option value="ios">iOS</option>
            <option value="android">Android</option>
          </select>

          <select
            value={analyzedFilter}
            onChange={(e) => setAnalyzedFilter(e.target.value)}
            className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-cyan-500"
          >
            <option value="">All Status</option>
            <option value="false">Not Analyzed</option>
            <option value="true">Analyzed</option>
          </select>
        </div>

        <button
          onClick={handleRefresh}
          className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      {/* Uploads List */}
      {uploads.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          <p className="text-sm font-medium text-gray-600 mb-1">No diagnostic uploads</p>
          <p className="text-xs text-gray-500">
            Support files will appear here when the user uploads diagnostic data from their device.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {uploads.map((upload) => (
            <SupportUploadCard
              key={upload.id}
              upload={upload}
              onUpdate={handleRefresh}
              isExpanded={expandedUploadId === upload.uploadId}
              onExpandChange={(isExpanded) => handleUploadExpand(upload.uploadId, isExpanded)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
